import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { downloadCsv } from '../lib/csv';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  ArrowRightLeft, TrendingUp, Banknote, CreditCard, Store,
  Truck, DollarSign, ShoppingCart, BarChart3, Receipt,
  Wallet, Building2, AlertTriangle, CheckCircle2, Clock,
  Landmark, Smartphone, ChevronRight, Filter, Search,
  RefreshCw, Eye, Check, X, Link2, Layers, Split, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';

// ─── Helpers ───
const fmt = (v, dec = 0) => {
  if (v == null || isNaN(v)) return '0';
  return Number(v).toLocaleString('en-IN', { maximumFractionDigits: dec, minimumFractionDigits: dec });
};
const fmtCur = (v) => `₹${fmt(v, 0)}`;
const fmtPct = (v) => `${fmt(v, 1)}%`;

// Shared color helpers — eliminates nested ternaries
const scoreColor = (s, type = 'text') => {
  const prefix = type === 'bg' ? 'bg' : 'text';
  if (s >= 75) return `${prefix}-emerald-400`;
  if (s >= 55) return `${prefix}-amber-400`;
  return `${prefix}-red-400`;
};
const varianceColor = (v) => v > 0 ? 'text-emerald-400' : v < 0 ? 'text-red-400' : 'text-zinc-400';
const varianceColorAlt = (v) => v > 0 ? 'text-red-400' : v < 0 ? 'text-emerald-400' : 'text-zinc-400';

const RAIL_ICONS = {
  CASH: Banknote, CARD: CreditCard, UPI: Smartphone,
  SWIGGY_PAY: Store, ZOMATO_PAY: Store, MIXED: ArrowRightLeft,
};
const RAIL_COLORS = {
  CASH: 'text-green-400 bg-green-500/10', CARD: 'text-blue-400 bg-blue-500/10',
  UPI: 'text-purple-400 bg-purple-500/10', SWIGGY_PAY: 'text-orange-400 bg-orange-500/10',
  ZOMATO_PAY: 'text-red-400 bg-red-500/10', MIXED: 'text-zinc-400 bg-zinc-500/10',
};
const CHANNEL_COLORS = {
  POS_DINE_IN: 'bg-emerald-500', SWIGGY_DELIVERY: 'bg-orange-500',
  ZOMATO_DELIVERY: 'bg-red-500', UPLOAD: 'bg-blue-500',
};
const PARTNER_COLORS = {
  ZOMATO: 'text-red-400', SWIGGY: 'text-orange-400',
  BANK: 'text-blue-400', NONE: 'text-zinc-400',
};

const BUCKET_CONFIG = {
  ZOMATO_DELIVERY: { label: 'Online Zomato', icon: Truck, color: 'text-red-400', bg: 'bg-red-500/10', bar: 'bg-red-500/60' },
  ZOMATO_DINE_IN_PARTNER_PAY: { label: 'Dine-In Zomato Pay', icon: Store, color: 'text-red-300', bg: 'bg-red-400/10', bar: 'bg-red-400/40' },
  SWIGGY_DELIVERY: { label: 'Online Swiggy', icon: Truck, color: 'text-orange-400', bg: 'bg-orange-500/10', bar: 'bg-orange-500/60' },
  SWIGGY_DINE_IN_PARTNER_PAY: { label: 'Dine-In Swiggy Pay', icon: Store, color: 'text-orange-300', bg: 'bg-orange-400/10', bar: 'bg-orange-400/40' },
  BANK_CARD_MDR: { label: 'Card MDR', icon: CreditCard, color: 'text-blue-400', bg: 'bg-blue-500/10', bar: 'bg-blue-500/60' },
  NONE_DIRECT: { label: 'Direct', icon: Banknote, color: 'text-emerald-400', bg: 'bg-emerald-500/10', bar: 'bg-emerald-500/60' },
  NONE_DIRECT_CASH: { label: 'Cash Collection', icon: Banknote, color: 'text-green-400', bg: 'bg-green-500/10', bar: 'bg-green-500/60' },
  NONE_DIRECT_UPI: { label: 'UPI Direct', icon: Smartphone, color: 'text-purple-400', bg: 'bg-purple-500/10', bar: 'bg-purple-500/60' },
};
const getBucket = (key) => BUCKET_CONFIG[key] || { label: key?.replace(/_/g, ' '), icon: ArrowRightLeft, color: 'text-zinc-400', bg: 'bg-zinc-500/10', bar: 'bg-zinc-500/60' };

const TABS = [
  { id: 'transactions', label: 'Transactions', icon: ArrowRightLeft },
  { id: 'settlements', label: 'Settlements', icon: Landmark },
  { id: 'partners', label: 'Partners', icon: Building2 },
  { id: 'reconciliation', label: 'Reconciliation', icon: AlertTriangle },
  { id: 'matching', label: 'Matching', icon: Link2 },
];

// ─── Stat Card ───
const StatCard = ({ label, value, sub, icon: Icon, color = 'text-zinc-100' }) => (
  <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] text-zinc-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-mono font-bold mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
        </div>
        {Icon && <Icon className="w-5 h-5 text-zinc-600" />}
      </div>
    </CardContent>
  </Card>
);

// ─── ExclusionBanner ───
// Surface NEEDS_MAPPING / NEEDS_REVIEW / QUARANTINED settlement counts
// alongside Settlement / Reconciliation / Matching tabs so operators
// see at a glance that totals exclude bad rows. Without this, a clean
// "₹417,255" total hides the fact that ₹1.23 B of pending data was
// quietly excluded. See settlement_validation.py for the contract.
const ExclusionBanner = ({ excluded, onRunReclassify, busy }) => {
  if (!excluded || Object.keys(excluded).length === 0) return null;
  const totalCount = Object.values(excluded).reduce((s, v) => s + (v?.count || 0), 0);
  const totalAmt = Object.values(excluded).reduce((s, v) => s + (v?.total_payout || 0), 0);
  if (totalCount === 0) return null;
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="exclusion-banner">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-400 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs font-medium text-amber-300">
            {totalCount} settlement row{totalCount === 1 ? '' : 's'} excluded from totals (₹{fmt(totalAmt, 0)} held back)
          </p>
          <div className="flex flex-wrap gap-3 mt-1.5 text-[11px] text-amber-200/80">
            {excluded.QUARANTINED && (
              <span data-testid="excluded-quarantined">
                <span className="text-amber-400 font-medium">QUARANTINED</span>: {excluded.QUARANTINED.count} row(s),
                ₹{fmt(excluded.QUARANTINED.total_payout, 0)}
              </span>
            )}
            {excluded.NEEDS_MAPPING && (
              <span data-testid="excluded-needs-mapping">
                <span className="text-amber-400 font-medium">NEEDS_MAPPING</span>: {excluded.NEEDS_MAPPING.count} row(s),
                ₹{fmt(excluded.NEEDS_MAPPING.total_payout, 0)}
              </span>
            )}
            {excluded.NEEDS_REVIEW && (
              <span data-testid="excluded-needs-review">
                <span className="text-amber-400 font-medium">NEEDS_REVIEW</span>: {excluded.NEEDS_REVIEW.count} row(s),
                ₹{fmt(excluded.NEEDS_REVIEW.total_payout, 0)}
              </span>
            )}
          </div>
          <p className="text-[10px] text-amber-200/60 mt-1.5">
            Quarantined rows are operator-flagged or auto-detected placeholder values. NEEDS_MAPPING rows have
            unresolved partner / commercial_model — re-upload with corrected column mapping. NEEDS_REVIEW rows
            exceed the implausible-amount threshold (₹1 cr).
          </p>
        </div>
        {onRunReclassify && (
          <button onClick={onRunReclassify} disabled={busy}
            className="text-[10px] font-medium text-amber-400 hover:text-amber-300 underline disabled:opacity-50 self-center"
            data-testid="reclassify-btn">
            {busy ? 'Reclassifying…' : 'Reclassify all'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── ContractGapBanner ───
// Surfaces partners that have orders but zero expected commission. Now
// distinguishes between two reason codes (set by transaction_router):
//   NO_ACTIVE_COMMISSION_CONFIG  — genuine contract data gap, operator
//                                  must seed `commission_configs`.
//   STALE_COMMISSION_PROJECTION  — config exists, denormalised fields
//                                  not yet refreshed; will self-heal
//                                  on next sync; operator can also
//                                  manually trigger recompute.
const ContractGapBanner = ({ gaps }) => {
  if (!gaps || gaps.length === 0) return null;
  const hasStale = gaps.some(g => g.reason_code === 'STALE_COMMISSION_PROJECTION');
  const tone = hasStale
    ? 'border-amber-500/30 bg-amber-500/5'
    : 'border-orange-500/30 bg-orange-500/5';
  const titleTone = hasStale ? 'text-amber-300' : 'text-orange-300';
  const bodyTone = hasStale ? 'text-amber-200/80' : 'text-orange-200/80';
  const iconTone = hasStale ? 'text-amber-400' : 'text-orange-400';
  return (
    <div className={`rounded-lg border p-3 ${tone}`} data-testid="contract-gap-banner">
      <div className="flex items-start gap-2.5">
        <ShieldAlert className={`w-4 h-4 mt-0.5 ${iconTone} flex-shrink-0`} />
        <div className="flex-1">
          <p className={`text-xs font-medium ${titleTone}`}>
            {hasStale
              ? `Commission projection drift (${gaps.length} partner${gaps.length === 1 ? '' : 's'})`
              : `Missing commission contract${gaps.length === 1 ? '' : 's'} (${gaps.length} partner${gaps.length === 1 ? '' : 's'})`}
          </p>
          <div className="space-y-1 mt-1.5">
            {gaps.map(g => (
              <p key={g.partner} className={`text-[11px] ${bodyTone}`}
                 data-testid={`contract-gap-${g.partner}-${g.reason_code}`}>
                <span className={`font-medium ${titleTone}`}>{g.partner}</span>: {g.orders} orders, ₹{fmt(g.net_amount, 0)} net —{' '}
                {g.reason_code === 'STALE_COMMISSION_PROJECTION'
                  ? 'contract exists but denormalised fields are stale. Will refresh on next sync; or POST /api/commission-configs/recompute to refresh now.'
                  : <>expected commission shows as ₹0. Seed an active row in <code className={`${iconTone} bg-black/20 px-1 rounded`}>commission_configs</code> to fix.</>}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── StaleMatchingBanner ───
// Tells the operator that the matching results displayed are out of
// date (a settlement was activated or a quarantine action was taken
// after the last run). Without this, "Health Score: 26.7" looks like
// a static fact when it's actually a stale snapshot.
const StaleMatchingBanner = ({ needsRerun, reason, onRunMatching, busy }) => {
  if (!needsRerun) return null;
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3" data-testid="stale-matching-banner">
      <div className="flex items-start gap-2.5">
        <RefreshCw className="w-4 h-4 mt-0.5 text-red-400 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs font-medium text-red-300">Matching results are stale</p>
          <p className="text-[11px] text-red-200/80 mt-0.5">{reason || 'Settlement state has changed since the last matching run.'}</p>
        </div>
        <button onClick={onRunMatching} disabled={busy}
          className="px-3 py-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[11px] font-medium rounded transition-colors"
          data-testid="rerun-matching-btn">
          {busy ? 'Running…' : 'Re-run matching'}
        </button>
      </div>
    </div>
  );
};

// ─── Bar (horizontal proportion) ───
const ProportionBar = ({ items, total }) => {
  if (!total || !items?.length) return null;
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-zinc-800 mt-2" data-testid="proportion-bar">
      {items.map((item, i) => (
        <div key={item.label || `bar-${i}`} className={`${item.color} transition-all`}
          style={{ width: `${Math.max((item.value / total) * 100, 1)}%` }}
          title={`${item.label}: ${fmtPct(item.value / total * 100)}`} />
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB 1: TRANSACTION OVERVIEW
// ═══════════════════════════════════════════════════════════════
const TransactionsTab = ({ summary, transactions, loading }) => {
  if (loading) return <LoadingSkeleton />;
  const t = summary?.totals || {};
  const channels = summary?.channel_mix || [];
  const models = summary?.model_mix || [];
  const dailyTrend = summary?.daily_trend || [];
  const rails = summary?.rail_mix || [];

  const totalChannelOrders = channels.reduce((s, c) => s + c.orders, 0);
  const maxDailyRev = Math.max(...dailyTrend.map(d => d.net_amount), 1);
  const totalRailGross = rails.reduce((s, r) => s + (r.gross_amount || 0), 0);

  return (
    <div className="space-y-5">
      {/* ── Sales Truth KPIs ──
          These mirror system-integrity exactly:
            sales = collected + unpaid
            core_sales = total_amount - tips
          Tips, service charge, discounts are surfaced separately so the
          Transactions page never silently folds a passthrough into
          revenue. */}
      <div data-testid="sales-truth-kpis">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Sales Truth — sourced from sc_transactions</p>
          <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-400">
            {summary?.source || 'SC_TRANSACTIONS'}
          </Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Gross Sales" value={fmtCur(t.total_gross)} icon={TrendingUp} sub={`${fmt(t.total_orders)} orders`} />
          <StatCard label="Net Sales" value={fmtCur(t.total_net)} icon={DollarSign} color="text-emerald-400" sub="excl. tax" />
          <StatCard label="Collected" value={fmtCur(t.collected_total)} icon={CheckCircle2} color="text-emerald-400" sub={`${fmt(t.paid_orders || 0)} paid orders`} />
          <StatCard label="Unpaid" value={fmtCur(t.unpaid_total)} icon={Clock} color={t.unpaid_total > 0 ? 'text-amber-400' : 'text-zinc-400'} sub={`${fmt(t.unpaid_orders || 0)} open orders`} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <StatCard label="Tax" value={fmtCur(t.total_tax)} icon={Receipt} color="text-zinc-300" />
          <StatCard label="Discounts" value={fmtCur(t.total_discount)} icon={Split} color={t.total_discount > 0 ? 'text-orange-400' : 'text-zinc-400'} />
          <StatCard label="Service Charge" value={fmtCur(t.total_service_charge)} icon={Layers} color="text-zinc-300" sub="staff pool" />
          <StatCard label="Tips" value={fmtCur(t.total_tips)} icon={Wallet} color={t.total_tips > 0 ? 'text-purple-400' : 'text-zinc-400'} sub="passthrough — not revenue" />
        </div>
      </div>

      {/* Settlement-flow KPIs (commission economics) */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-2">Settlement Economics</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="AOV" value={fmtCur(t.aov)} icon={BarChart3} sub="avg order value" />
          <StatCard label="Commission" value={fmtCur(t.total_commission)} icon={Receipt} color="text-amber-400" sub={`+${fmtCur(t.total_commission_gst)} GST`} />
          <StatCard label="Net Settlement" value={fmtCur(t.total_expected_settlement)} icon={Wallet} color="text-blue-400" />
          <StatCard label="Items" value={fmt(t.total_items)} icon={ShoppingCart} sub="line items" />
        </div>
      </div>

      {/* Payment Rails — explicit breakdown, paid orders only */}
      {rails.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="payment-rails-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-emerald-400" />
              Payment Rails
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {rails.map(r => {
                const RIcon = RAIL_ICONS[r.rail] || ArrowRightLeft;
                const rcolor = RAIL_COLORS[r.rail] || 'text-zinc-400 bg-zinc-500/10';
                const pct = totalRailGross > 0 ? (r.gross_amount / totalRailGross * 100) : 0;
                return (
                  <div key={r.rail || 'unknown-rail'} className="p-3 rounded-lg bg-zinc-800/30 border border-zinc-800/50" data-testid={`rail-${r.rail || 'unknown'}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${rcolor}`}>
                        <RIcon className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-zinc-200">{r.rail || 'Unknown'}</p>
                        <p className="text-[9px] text-zinc-500">{r.orders} orders · {fmtPct(pct)}</p>
                      </div>
                    </div>
                    <p className="text-base font-mono font-bold text-zinc-100">{fmtCur(r.gross_amount)}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Channel Mix */}
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300">Source Mix</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ProportionBar total={totalChannelOrders}
              items={channels.map(c => ({
                label: c.channel, value: c.orders,
                color: CHANNEL_COLORS[c.channel] || 'bg-zinc-500',
              }))} />
            <div className="space-y-2 mt-4">
              {channels.map(c => {
                const pct = totalChannelOrders ? (c.orders / totalChannelOrders * 100) : 0;
                return (
                  <div key={c.channel} className="flex items-center justify-between" data-testid={`channel-${c.channel}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${CHANNEL_COLORS[c.channel] || 'bg-zinc-500'}`} />
                      <span className="text-sm text-zinc-300">{c.channel?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-zinc-500">{c.orders} orders</span>
                      <span className="text-zinc-400 font-mono w-12 text-right">{fmtPct(pct)}</span>
                      <span className="text-zinc-100 font-mono w-24 text-right">{fmtCur(c.net_amount)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Model Mix */}
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300">Commercial Model Mix</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3 mt-2">
              {models.map(m => {
                const eff = m.net_amount ? (m.commission / m.net_amount * 100) : 0;
                return (
                  <div key={m.model} className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-800/60" data-testid={`model-${m.model}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {m.model === 'DELIVERY' && <Truck className="w-3.5 h-3.5 text-zinc-400" />}
                        {m.model === 'DINE_IN_PARTNER_PAY' && <Store className="w-3.5 h-3.5 text-zinc-400" />}
                        {m.model === 'CARD_MDR' && <CreditCard className="w-3.5 h-3.5 text-zinc-400" />}
                        {m.model === 'DIRECT' && <Banknote className="w-3.5 h-3.5 text-zinc-400" />}
                        {m.model === 'DIRECT_CASH' && <Banknote className="w-3.5 h-3.5 text-green-400" />}
                        {m.model === 'DIRECT_UPI' && <Smartphone className="w-3.5 h-3.5 text-purple-400" />}
                        <span className="text-sm text-zinc-200">{m.model?.replace(/_/g, ' ')}</span>
                      </div>
                      <span className="text-xs text-zinc-500">{m.orders} orders</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className="text-zinc-400">Revenue: <span className="text-zinc-200 font-mono">{fmtCur(m.net_amount)}</span></span>
                      <span className="text-zinc-400">Commission: <span className="text-amber-400 font-mono">{fmtCur(m.commission)}</span></span>
                      <span className="text-zinc-500">Eff. {fmtPct(eff)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend */}
      {dailyTrend.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300">Daily Revenue Trend</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-end gap-1 h-32 mt-2" data-testid="daily-trend-chart">
              {dailyTrend.map((d, i) => {
                const h = (d.net_amount / maxDailyRev) * 100;
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${fmtCur(d.net_amount)} (${d.orders} orders)`}>
                    <div className="w-full bg-emerald-500/70 rounded-t transition-all group-hover:bg-emerald-400"
                      style={{ height: `${Math.max(h, 2)}%` }} />
                    {dailyTrend.length <= 15 && (
                      <span className="text-[8px] text-zinc-600 -rotate-45 origin-top-left mt-1">
                        {d.date?.slice(5)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions Table */}
      <Card className="bg-zinc-900/40 border-zinc-800/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-300">Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="transactions-table">
              <thead>
                <tr className="border-b border-zinc-800 text-zinc-500">
                  <th className="text-left py-2 pr-3">Date</th>
                  <th className="text-left py-2 pr-3">Source</th>
                  <th className="text-left py-2 pr-3">Model</th>
                  <th className="text-left py-2 pr-3">Payment</th>
                  <th className="text-right py-2 pr-3">Net</th>
                  <th className="text-right py-2 pr-3">Commission</th>
                  <th className="text-right py-2">Settlement</th>
                </tr>
              </thead>
              <tbody>
                {(transactions || []).slice(0, 20).map((txn, i) => (
                  <tr key={txn.transaction_id || i} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                    <td className="py-2 pr-3 text-zinc-400">{txn.date}</td>
                    <td className="py-2 pr-3 text-zinc-300">{txn.order_origin?.replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-400">{txn.commercial_model}</Badge>
                    </td>
                    <td className="py-2 pr-3 text-zinc-400">{txn.settlement_rail}</td>
                    <td className="py-2 pr-3 text-right font-mono text-zinc-200">{fmtCur(txn.net_amount)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-amber-400">{fmtCur(txn.expected_commission)}</td>
                    <td className="py-2 text-right font-mono text-emerald-400">{fmtCur(txn.expected_net_settlement)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!transactions || transactions.length === 0) && (
            <p className="text-center text-zinc-600 py-6 text-sm">No transactions found for this period</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB 2: SETTLEMENT DASHBOARD
// ═══════════════════════════════════════════════════════════════
// Bucket visibility helper — Cash/UPI Direct are reconcilable
const isReconVisible = (r) => r.partner !== 'NONE' || r.commercial_model === 'DIRECT_CASH' || r.commercial_model === 'DIRECT_UPI';

// ═══════════════════════════════════════════════════════════════
const SettlementsTab = ({ summary, loading }) => {
  if (loading) return <LoadingSkeleton />;
  const pmMix = summary?.partner_model_mix || [];
  const statuses = summary?.settlement_mix || [];
  const t = summary?.totals || {};

  // Categorize buckets for KPIs
  const directBuckets = pmMix.filter(b => b.commercial_model === 'DIRECT' || b.commercial_model === 'DIRECT_CASH' || b.commercial_model === 'DIRECT_UPI');
  const cardBuckets = pmMix.filter(b => b.commercial_model === 'CARD_MDR');
  const partnerBuckets = pmMix.filter(b => ['DELIVERY', 'DINE_IN_PARTNER_PAY'].includes(b.commercial_model) && b.partner !== 'NONE');

  const directSettlement = directBuckets.reduce((s, b) => s + b.expected_net_settlement, 0);
  const cashSettlement = pmMix.filter(b => b.commercial_model === 'DIRECT_CASH').reduce((s, b) => s + b.expected_net_settlement, 0);
  const upiDirectSettlement = pmMix.filter(b => b.commercial_model === 'DIRECT_UPI').reduce((s, b) => s + b.expected_net_settlement, 0);
  const cardSettlement = cardBuckets.reduce((s, b) => s + b.expected_net_settlement, 0);
  const partnerSettlement = partnerBuckets.reduce((s, b) => s + b.expected_net_settlement, 0);

  return (
    <div className="space-y-5">
      {/* Settlement KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total Expected" value={fmtCur(t.total_expected_settlement)} icon={Wallet} color="text-emerald-400" />
        <StatCard label="Cash Collection" value={fmtCur(cashSettlement)} icon={Banknote} color="text-green-400" sub="cash at counter" />
        <StatCard label="UPI Direct" value={fmtCur(upiDirectSettlement)} icon={Smartphone} color="text-purple-400" sub="QR / UPI payments" />
        <StatCard label="Bank (Card MDR)" value={fmtCur(cardSettlement)} icon={Landmark} color="text-blue-400" sub="card settlements" />
        <StatCard label="Partner Receivable" value={fmtCur(partnerSettlement)} icon={Building2} color="text-orange-400" sub="delivery + dine-in pay" />
      </div>

      {/* Settlement by Partner × Model */}
      <Card className="bg-zinc-900/40 border-zinc-800/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-300">Settlement by Commercial Bucket</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            {pmMix.map(b => {
              const cfg = getBucket(b.bucket_key);
              const BIcon = cfg.icon;
              const totalSettlement = t.total_expected_settlement || 1;
              const pct = (b.expected_net_settlement / totalSettlement) * 100;
              return (
                <div key={b.bucket_key} className="p-3 rounded-lg bg-zinc-800/30 border border-zinc-800/50" data-testid={`bucket-${b.bucket_key}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                        <BIcon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div>
                        <span className={`text-sm font-medium ${cfg.color}`}>{cfg.label}</span>
                        <p className="text-[10px] text-zinc-500">{b.orders} orders &middot; {fmtPct(b.effective_pct)} eff. commission</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-mono font-bold text-zinc-100">{fmtCur(b.expected_net_settlement)}</p>
                      <p className="text-[10px] text-zinc-500">{fmtPct(pct)} of total</p>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${cfg.bar}`} style={{ width: `${Math.max(pct, 0.5)}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-[10px] text-zinc-500">
                    <span>Net: {fmtCur(b.net_amount)}</span>
                    <span>Commission: {fmtCur(b.expected_commission)}</span>
                    <span>GST on Comm: {fmtCur(b.expected_tax_on_commission)}</span>
                    <span>Settlement: {fmtCur(b.expected_net_settlement)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Settlement Status */}
      <Card className="bg-zinc-900/40 border-zinc-800/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-300">Settlement Status</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {statuses.map(s => {
              const isSettled = s.status === 'SETTLED' || s.status === 'RECEIVED';
              const isPending = s.status === 'PENDING' || s.status === 'EXPECTED';
              return (
                <div key={s.status} className={`p-4 rounded-lg border ${isSettled ? 'bg-emerald-950/20 border-emerald-500/20' : isPending ? 'bg-amber-950/20 border-amber-500/20' : 'bg-zinc-800/30 border-zinc-800/50'}`}
                  data-testid={`settlement-status-${s.status}`}>
                  <div className="flex items-center gap-2">
                    {isSettled ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> :
                     isPending ? <Clock className="w-4 h-4 text-amber-400" /> :
                     <AlertTriangle className="w-4 h-4 text-zinc-400" />}
                    <span className="text-xs font-medium text-zinc-300">{s.status}</span>
                  </div>
                  <p className="text-xl font-mono font-bold text-zinc-100 mt-2">{fmtCur(s.amount)}</p>
                  <p className="text-[10px] text-zinc-500 mt-1">{s.count} transactions</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB 3: PARTNER DASHBOARD
// ═══════════════════════════════════════════════════════════════
const PartnersTab = ({ summary, loading }) => {
  if (loading) return <LoadingSkeleton />;
  const pmMix = (summary?.partner_model_mix || []).filter(b => b.partner !== 'NONE');
  const totalNet = pmMix.reduce((s, b) => s + b.net_amount, 0);
  const totalComm = pmMix.reduce((s, b) => s + b.expected_commission, 0);
  const totalSettlement = pmMix.reduce((s, b) => s + b.expected_net_settlement, 0);
  const contractGaps = summary?.contract_gaps || [];

  return (
    <div className="space-y-5">
      {/* Contract data gaps — surfaces partners with orders but no
          active commission_configs row so the operator sees that 0%
          effective rate is a contract gap, not a free contract.
          Distinguishes STALE_COMMISSION_PROJECTION from
          NO_ACTIVE_COMMISSION_CONFIG. */}
      <ContractGapBanner gaps={contractGaps} />

      {/* Commission freshness pill — shows whether the persisted
          expected_commission fields are aligned with the current
          commission_configs. The auto-tail on every sc_transaction_sync
          keeps this FRESH; STALE fires only if the recompute tail
          itself failed (and the watermark records the error). */}
      {summary?.commission_freshness && summary.commission_freshness !== 'NO_DATA' && (
        <div className="flex items-center gap-2 text-[11px]" data-testid="commission-freshness-strip">
          <span className="text-zinc-500">Commission projection:</span>
          <span
            className={
              summary.commission_freshness === 'FRESH'
                ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                : summary.commission_freshness === 'STALE'
                ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30'
                : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-700/30 text-zinc-400 border border-zinc-700/40'
            }
            data-testid={`commission-freshness-${summary.commission_freshness.toLowerCase()}`}
          >
            <span className={
              summary.commission_freshness === 'FRESH' ? 'w-1.5 h-1.5 rounded-full bg-emerald-400' :
              summary.commission_freshness === 'STALE' ? 'w-1.5 h-1.5 rounded-full bg-amber-400' :
              'w-1.5 h-1.5 rounded-full bg-zinc-500'
            } />
            {summary.commission_freshness}
          </span>
          <span className="text-zinc-600">
            {summary.commission_freshness === 'FRESH'
              ? 'all partner buckets up to date'
              : summary.commission_freshness === 'STALE'
              ? 'last recompute failed — re-run /api/commission-configs/recompute'
              : 'no recompute watermark yet (legacy state)'}
          </span>
        </div>
      )}

      {/* Partner KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Partner Revenue" value={fmtCur(totalNet)} icon={DollarSign} color="text-emerald-400" />
        <StatCard label="Total Commission" value={fmtCur(totalComm)} icon={Receipt} color="text-amber-400" />
        <StatCard label="Blended Eff. Rate" value={fmtPct(totalNet ? totalComm / totalNet * 100 : 0)} icon={TrendingUp} />
        <StatCard label="Net Receivable" value={fmtCur(totalSettlement)} icon={Wallet} color="text-blue-400" />
      </div>

      {/* Per-Bucket Cards */}
      <div className="space-y-3">
        {pmMix.map(b => {
          const cfg = getBucket(b.bucket_key);
          const BIcon = cfg.icon;
          const revShare = totalNet ? (b.net_amount / totalNet * 100) : 0;
          return (
            <Card key={b.bucket_key} className="bg-zinc-900/40 border-zinc-800/40" data-testid={`partner-bucket-${b.bucket_key}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                      <BIcon className={`w-5 h-5 ${cfg.color}`} />
                    </div>
                    <div>
                      <h3 className={`text-base font-semibold ${cfg.color}`}>{cfg.label}</h3>
                      <p className="text-[11px] text-zinc-500">{b.orders} orders &middot; {fmtPct(revShare)} of partner revenue</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-500">
                    {b.commercial_model?.replace(/_/g, ' ')}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-3 border-t border-zinc-800/50">
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Revenue</p>
                    <p className="text-sm font-mono font-bold text-zinc-100 mt-0.5">{fmtCur(b.net_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Commission</p>
                    <p className="text-sm font-mono font-bold text-amber-400 mt-0.5">{fmtCur(b.expected_commission)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">GST on Comm</p>
                    <p className="text-sm font-mono font-bold text-zinc-300 mt-0.5">{fmtCur(b.expected_tax_on_commission)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Effective %</p>
                    <p className="text-sm font-mono font-bold text-zinc-100 mt-0.5">{fmtPct(b.effective_pct)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Net Settlement</p>
                    <p className="text-sm font-mono font-bold text-emerald-400 mt-0.5">{fmtCur(b.expected_net_settlement)}</p>
                  </div>
                </div>

                {/* Revenue share bar */}
                <div className="mt-3 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${cfg.bar}`}
                    style={{ width: `${Math.max(revShare, 0.5)}%` }} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB 4: RECONCILIATION DASHBOARD
// ═══════════════════════════════════════════════════════════════

const STATUS_STYLES = {
  MATCHED: { bg: 'bg-emerald-950/20 border-emerald-500/20', text: 'text-emerald-400', badge: 'border-emerald-500/30 text-emerald-400' },
  SHORTFALL: { bg: 'bg-red-950/20 border-red-500/20', text: 'text-red-400', badge: 'border-red-500/30 text-red-400' },
  OVERPAID: { bg: 'bg-blue-950/20 border-blue-500/20', text: 'text-blue-400', badge: 'border-blue-500/30 text-blue-400' },
  AWAITING_SETTLEMENT: { bg: 'bg-amber-950/20 border-amber-500/20', text: 'text-amber-400', badge: 'border-amber-500/30 text-amber-400' },
  UNEXPECTED_SETTLEMENT: { bg: 'bg-purple-950/20 border-purple-500/20', text: 'text-purple-400', badge: 'border-purple-500/30 text-purple-400' },
  DELAYED: { bg: 'bg-yellow-950/20 border-yellow-500/20', text: 'text-yellow-400', badge: 'border-yellow-500/30 text-yellow-400' },
  OVERDUE: { bg: 'bg-red-950/20 border-red-500/20', text: 'text-red-400', badge: 'border-red-500/30 text-red-400' },
  EXCESS: { bg: 'bg-blue-950/20 border-blue-500/20', text: 'text-blue-400', badge: 'border-blue-500/30 text-blue-400' },
  UNMATCHED_ROW: { bg: 'bg-purple-950/20 border-purple-500/20', text: 'text-purple-400', badge: 'border-purple-500/30 text-purple-400' },
  MANY_TO_ONE_REVIEW: { bg: 'bg-orange-950/20 border-orange-500/20', text: 'text-orange-400', badge: 'border-orange-500/30 text-orange-400' },
  ONE_TO_MANY_REVIEW: { bg: 'bg-cyan-950/20 border-cyan-500/20', text: 'text-cyan-400', badge: 'border-cyan-500/30 text-cyan-400' },
};

const ReconciliationTab = ({ summary, loading, dateFrom, dateTo }) => {
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadStep, setUploadStep] = useState('IDLE');  // IDLE | UPLOADING | MAPPING_READY | CONFIRMING | PREVIEW_READY | ACTIVATING | COMPLETE | ERROR
  const [uploadState, setUploadState] = useState({});
  const [mapping, setMapping] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sourceOverride, setSourceOverride] = useState('');
  const [batchPeriodStart, setBatchPeriodStart] = useState('');
  const [batchPeriodEnd, setBatchPeriodEnd] = useState('');
  const [batchModel, setBatchModel] = useState('');
  const [reportType, setReportType] = useState('PARTNER_SETTLEMENT');
  const [batchModeOfPayment, setBatchModeOfPayment] = useState('');
  const [uploadError, setUploadError] = useState(null);  // { stage, message, detail }
  const [anomalies, setAnomalies] = useState([]);
  const [anomalySummary, setAnomalySummary] = useState({});
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [commIntegrity, setCommIntegrity] = useState(null);
  const [reclassifying, setReclassifying] = useState(false);

  const loadRecon = useCallback(async () => {
    setReconLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [reconRes, uploadsRes, anomalyRes, commRes] = await Promise.all([
        accountsAPI.getSettlementReconciliation(params),
        accountsAPI.getSettlementUploads(),
        accountsAPI.getAnomalies(params),
        accountsAPI.getCommissionIntegrity(params).catch(() => ({ data: null })),
      ]);
      setReconData(reconRes.data);
      setUploads(uploadsRes.data?.uploads || []);
      setAnomalies(anomalyRes.data?.anomalies || []);
      setAnomalySummary(anomalyRes.data?.summary || {});
      setCommIntegrity(commRes.data);
    } catch (e) {
      toast.error('Failed to load reconciliation data');
    } finally {
      setReconLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadRecon(); }, [loadRecon]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploadStep('UPLOADING');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('report_type', reportType);
      if (sourceOverride) formData.append('settlement_source', sourceOverride);
      if (sourceOverride) formData.append('partner', sourceOverride);
      if (batchPeriodStart) formData.append('period_start', batchPeriodStart);
      if (batchPeriodEnd) formData.append('period_end', batchPeriodEnd);
      if (batchModel) formData.append('commercial_model', batchModel);
      if (batchModeOfPayment) formData.append('mode_of_payment', batchModeOfPayment);
      const res = await accountsAPI.uploadSettlementFile(formData);
      const data = res.data;
      if (!data || !data.upload_id) {
        throw new Error('Upload returned empty response — no upload_id received');
      }
      setUploadState(data);
      setMapping(data.detected_column_mapping || {});
      setUploadStep('MAPPING_READY');
      toast.success(`Uploaded ${data.total_rows} rows from ${data.file_name}`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Upload failed';
      setUploadError({ stage: 'File Upload', message: detail, detail: `File: ${file.name} (${(file.size / 1024).toFixed(1)} KB, ${file.type || 'unknown MIME'})` });
      setUploadStep('ERROR');
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmMapping = async () => {
    setUploadError(null);
    setUploadStep('CONFIRMING');
    setUploading(true);
    try {
      const res = await accountsAPI.saveSettlementColumnMapping(uploadState.upload_id, { column_mapping: mapping });
      if (!res.data) {
        throw new Error('Mapping confirmation returned empty response');
      }
      setUploadState(prev => ({ ...prev, ...res.data }));
      // Load preview
      const preview = await accountsAPI.previewSettlementRows(uploadState.upload_id, { limit: 20 });
      const rows = preview.data?.rows || [];
      setPreviewRows(rows);
      setUploadStep('PREVIEW_READY');
      toast.success(`Mapped ${res.data.valid_rows} valid rows`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Mapping failed';
      setUploadError({ stage: 'Column Mapping', message: detail, detail: `Upload: ${uploadState.file_name}` });
      setUploadStep('ERROR');
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleActivate = async () => {
    setUploadError(null);
    setUploadStep('ACTIVATING');
    setUploading(true);
    try {
      const body = {};
      if (sourceOverride) body.settlement_source = sourceOverride;
      const res = await accountsAPI.activateSettlementUpload(uploadState.upload_id, body);
      if (!res.data) {
        throw new Error('Activation returned empty response');
      }
      toast.success(`Imported ${res.data.settlements_created} settlement records`);
      setUploadStep('COMPLETE');
      setTimeout(() => {
        setUploadStep('IDLE');
        setShowUpload(false);
        setUploadState({});
        setUploadError(null);
        loadRecon();
      }, 1500);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Activation failed';
      setUploadError({ stage: 'Activate & Import', message: detail, detail: `Upload: ${uploadState.file_name}, Rows: ${uploadState.valid_rows || 0}` });
      setUploadStep('ERROR');
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleDrilldown = async (bucketKey) => {
    if (drilldown?.bucket_key === bucketKey) { setDrilldown(null); return; }
    setDrilldownLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await accountsAPI.getAnomalyDrilldown(bucketKey, params);
      setDrilldown(res.data);
    } catch (err) {
      toast.error('Failed to load drilldown');
    } finally {
      setDrilldownLoading(false);
    }
  };

  const handleReclassify = async () => {
    setReclassifying(true);
    try {
      const res = await accountsAPI.reclassifySettlements();
      const t = res.data?.transitions || {};
      toast.success(
        `Reclassified: ${t.to_NEEDS_MAPPING || 0} mapping, ${t.to_NEEDS_REVIEW || 0} review, ${t.to_QUARANTINED || 0} quarantined`
      );
      await loadRecon();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reclassify failed');
    } finally {
      setReclassifying(false);
    }
  };

  const visibleRecon = useMemo(() => {
    const r = reconData?.reconciliation || [];
    return r.filter(isReconVisible);
  }, [reconData]);
  const visibleOrderTotal = useMemo(() => visibleRecon.reduce((s, r) => s + (r.expected_orders || 0), 0), [visibleRecon]);

  if (loading || reconLoading) return <LoadingSkeleton />;

  const recon = reconData?.reconciliation || [];
  const totals = reconData?.totals || {};
  const hasData = reconData?.has_settlement_data;

  const CANONICAL_FIELDS = {
    settlement_date: 'Settlement Date', payout_date: 'Payout Date', transaction_reference: 'Transaction Reference',
    partner_reference: 'Partner Reference', invoice_number: 'Invoice / Order ID', statement_number: 'Statement Number',
    settlement_source: 'Settlement Source', partner: 'Partner', commercial_model: 'Commercial Model',
    gross_settlement_amount: 'Gross Amount', commission_amount: 'Commission', gst_on_commission: 'GST on Commission',
    other_deductions: 'Other Deductions', net_payout_amount: 'Net Payout', tds_amount: 'TDS',
    refunds: 'Refunds / Reversals', order_count: 'Order Count', business_date: 'Business Date',
    source_order_number: 'Source Order Number', payment_rail: 'Payment Rail', remarks: 'Remarks',
    bank_reference: 'Bank Reference', credit_amount: 'Credit Amount', debit_amount: 'Debit Amount',
    closing_balance: 'Closing Balance',
    mode_of_payment: 'Mode of Payment', acquirer: 'Acquirer / Acquiring Bank', terminal_provider: 'Terminal Provider',
  };

  return (
    <div className="space-y-5">
      {/* Settlement quarantine state — surfaces excluded rows so operators
          never see clean totals while ₹1.23 B of bad data sits hidden. */}
      <ExclusionBanner
        excluded={reconData?.excluded_settlements}
        onRunReclassify={handleReclassify}
        busy={reclassifying}
      />

      {/* Upload Section */}
      <Card className="bg-zinc-900/40 border-zinc-800/40">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Landmark className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-sm font-medium text-zinc-200">Settlement Data Ingestion</p>
                <p className="text-[11px] text-zinc-500">Upload bank statements, Swiggy/Zomato remittance files</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {uploads.length > 0 && (
                <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-400" data-testid="upload-count-badge">
                  {uploads.filter(u => u.status === 'ACTIVATED').length} active file(s)
                </Badge>
              )}
              <button onClick={() => { setShowUpload(!showUpload); setUploadStep('IDLE'); setUploadError(null); }}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded-lg transition-colors"
                data-testid="upload-settlement-btn">
                {showUpload ? 'Close' : 'Upload Settlement File'}
              </button>
            </div>
          </div>

          {showUpload && uploadStep === 'IDLE' && (
            <div className="mt-4 pt-4 border-t border-zinc-800 space-y-3" data-testid="settlement-upload-form">
              {/* Step Indicator */}
              <div className="flex items-center gap-1 mb-2">
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-400 font-medium">Step 1: Upload File</span>
                  <ChevronRight className="w-3 h-3 text-zinc-600" />
                  <span className="px-2 py-0.5 rounded text-zinc-600">Step 2: Confirm Mapping</span>
                  <ChevronRight className="w-3 h-3 text-zinc-600" />
                  <span className="px-2 py-0.5 rounded text-zinc-600">Step 3: Review & Import</span>
                </div>
              </div>
              {/* Report Type Selection */}
              <div>
                <p className="text-[10px] text-zinc-500 mb-1.5 font-medium">Report Type</p>
                <div className="flex gap-2">
                  <button onClick={() => setReportType('PARTNER_SETTLEMENT')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs transition-colors border ${reportType === 'PARTNER_SETTLEMENT' ? 'bg-emerald-600/20 border-emerald-500/40 text-emerald-400' : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}
                    data-testid="report-type-partner">
                    Partner Settlement Report
                  </button>
                  <button onClick={() => setReportType('BANK_MDR')}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs transition-colors border ${reportType === 'BANK_MDR' ? 'bg-amber-600/20 border-amber-500/40 text-amber-400' : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}
                    data-testid="report-type-bank">
                    Bank Credit Card / MDR Report
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <p className="text-[10px] text-zinc-500 mb-1">Source / Partner *</p>
                  <select value={sourceOverride} onChange={e => setSourceOverride(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-lg px-3 py-2"
                    data-testid="source-select">
                    <option value="">Select source...</option>
                    {reportType === 'PARTNER_SETTLEMENT' ? (
                      <>
                        <option value="ZOMATO">Zomato</option>
                        <option value="SWIGGY">Swiggy</option>
                        <option value="RAZORPAY">Razorpay</option>
                        <option value="OTHER">Other</option>
                      </>
                    ) : (
                      <>
                        <option value="BANK">Bank Statement</option>
                        <option value="HDFC">HDFC</option>
                        <option value="ICICI">ICICI</option>
                        <option value="RAZORPAY">Razorpay</option>
                        <option value="OTHER">Other</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500 mb-1">Period Start *</p>
                  <Input type="date" value={batchPeriodStart} onChange={e => setBatchPeriodStart(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-xs" data-testid="period-start-input" />
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500 mb-1">Period End *</p>
                  <Input type="date" value={batchPeriodEnd} onChange={e => setBatchPeriodEnd(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-xs" data-testid="period-end-input" />
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500 mb-1">Commercial Model</p>
                  <select value={batchModel} onChange={e => setBatchModel(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-lg px-3 py-2"
                    data-testid="model-select">
                    <option value="">Auto / Mixed</option>
                    <option value="DELIVERY">Delivery</option>
                    <option value="DINE_IN_PARTNER_PAY">Dine-in Partner Pay</option>
                    <option value="CARD_MDR">Card / MDR</option>
                  </select>
                </div>
              </div>
              {/* Mode of Payment — visible for BANK_MDR reports */}
              {reportType === 'BANK_MDR' && (
                <div>
                  <p className="text-[10px] text-zinc-500 mb-1.5 font-medium">Mode of Payment</p>
                  <div className="flex gap-2">
                    <button onClick={() => setBatchModeOfPayment('')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs transition-colors border ${!batchModeOfPayment ? 'bg-zinc-600/20 border-zinc-500/40 text-zinc-300' : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}
                      data-testid="mode-auto">
                      Auto-detect
                    </button>
                    <button onClick={() => setBatchModeOfPayment('CARD')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs transition-colors border ${batchModeOfPayment === 'CARD' ? 'bg-blue-600/20 border-blue-500/40 text-blue-400' : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}
                      data-testid="mode-card">
                      <CreditCard className="w-3 h-3 inline mr-1" />Card
                    </button>
                    <button onClick={() => setBatchModeOfPayment('UPI')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs transition-colors border ${batchModeOfPayment === 'UPI' ? 'bg-purple-600/20 border-purple-500/40 text-purple-400' : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}
                      data-testid="mode-upi">
                      <Smartphone className="w-3 h-3 inline mr-1" />UPI
                    </button>
                    <button onClick={() => setBatchModeOfPayment('MIXED')}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs transition-colors border ${batchModeOfPayment === 'MIXED' ? 'bg-amber-600/20 border-amber-500/40 text-amber-400' : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}
                      data-testid="mode-mixed">
                      <ArrowRightLeft className="w-3 h-3 inline mr-1" />Mixed
                    </button>
                  </div>
                  {batchModeOfPayment === 'MIXED' && (
                    <p className="text-[10px] text-zinc-500 mt-1">Row-level mode will be auto-classified from file data</p>
                  )}
                </div>
              )}
              <label className="block cursor-pointer">
                <span className="block px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 border border-dashed border-zinc-600 rounded-lg text-center text-xs text-zinc-400 transition-colors">
                  {uploading ? 'Uploading...' : 'Choose CSV or XLSX file'}
                </span>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden"
                  disabled={uploading || !sourceOverride} data-testid="settlement-file-input" />
              </label>
              {!sourceOverride && <p className="text-[10px] text-amber-400">Please select a source before uploading</p>}
            </div>
          )}

          {/* Uploading State */}
          {showUpload && uploadStep === 'UPLOADING' && (
            <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-center py-8" data-testid="upload-progress">
              <div className="text-center space-y-2">
                <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-xs text-zinc-400">Parsing file and detecting columns...</p>
              </div>
            </div>
          )}

          {/* Column Mapping Step */}
          {(uploadStep === 'MAPPING_READY' || uploadStep === 'CONFIRMING') && (
            <div className="mt-4 pt-4 border-t border-zinc-800 space-y-3" data-testid="settlement-mapping-step">
              {/* Step Indicator */}
              <div className="flex items-center gap-1 mb-1">
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="px-2 py-0.5 rounded text-zinc-500">Step 1: Upload File</span>
                  <ChevronRight className="w-3 h-3 text-zinc-600" />
                  <span className="px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-400 font-medium">Step 2: Confirm Mapping</span>
                  <ChevronRight className="w-3 h-3 text-zinc-600" />
                  <span className="px-2 py-0.5 rounded text-zinc-600">Step 3: Review & Import</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-300 font-medium">Map Columns — {uploadState.file_name} ({uploadState.total_rows} rows)</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setUploadStep('IDLE'); setUploadState({}); setMapping({}); }}
                    className="px-3 py-1.5 text-zinc-400 hover:text-zinc-300 text-xs rounded-lg transition-colors"
                    data-testid="back-to-upload-btn">
                    Back
                  </button>
                  <button onClick={handleConfirmMapping} disabled={uploading || uploadStep === 'CONFIRMING'}
                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs rounded-lg font-medium"
                    data-testid="confirm-mapping-btn">
                    {uploadStep === 'CONFIRMING' ? (
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : 'Confirm Mapping'}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                {(uploadState.headers || []).map(header => (
                  <div key={header} className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded-lg">
                    <span className="text-[10px] text-zinc-400 w-28 truncate" title={header}>{header}</span>
                    <ChevronRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                    <select value={mapping[header] || '__skip__'}
                      onChange={e => setMapping(prev => ({...prev, [header]: e.target.value === '__skip__' ? undefined : e.target.value}))}
                      className="flex-1 bg-zinc-900 border border-zinc-700 text-zinc-300 text-[10px] rounded px-1.5 py-1"
                      data-testid={`map-${header.replace(/\s+/g, '-')}`}>
                      <option value="__skip__">-- skip --</option>
                      {Object.entries(CANONICAL_FIELDS).map(([field, label]) => (
                        <option key={field} value={field}>{label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {/* Sample rows */}
              {uploadState.sample_rows?.length > 0 && (
                <div className="overflow-x-auto">
                  <p className="text-[10px] text-zinc-500 mb-1">Sample data (first 3 rows)</p>
                  <table className="w-full text-[9px] text-zinc-400">
                    <thead>
                      <tr className="border-b border-zinc-800">{(uploadState.headers || []).map(h => <th key={h} className="text-left py-1 pr-2 text-zinc-500">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {uploadState.sample_rows.slice(0, 3).map((row, i) => (
                        <tr key={`sample-${i}-${row[(uploadState.headers || [])[0]] || i}`} className="border-b border-zinc-800/40">
                          {(uploadState.headers || []).map(h => <td key={h} className="py-1 pr-2 truncate max-w-[120px]">{row[h]}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* File metadata strip */}
              {uploadState.file_meta && (
                <div className="flex flex-wrap gap-3 text-[9px] text-zinc-500 bg-zinc-800/30 rounded-lg px-3 py-1.5" data-testid="file-meta-strip">
                  <span>Type: <span className="text-zinc-400">{uploadState.file_meta.extension}</span></span>
                  <span>Size: <span className="text-zinc-400">{((uploadState.file_meta.file_size_bytes || 0) / 1024).toFixed(1)} KB</span></span>
                  {uploadState.file_meta.sheet_count > 1 && (
                    <span>Sheets: <span className="text-zinc-400">{uploadState.file_meta.sheet_count} (using: {uploadState.file_meta.selected_sheet})</span></span>
                  )}
                  <span>Headers: <span className="text-zinc-400">{(uploadState.file_meta.detected_headers || []).length}</span></span>
                  <span>Rows: <span className="text-zinc-400">{uploadState.file_meta.data_row_count}</span></span>
                  {uploadState.file_meta.banner_rows_skipped > 0 && (
                    <span>Skipped: <span className="text-amber-400">{uploadState.file_meta.banner_rows_skipped} banner rows</span></span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Preview Step */}
          {(uploadStep === 'PREVIEW_READY' || uploadStep === 'ACTIVATING') && (
            <div className="mt-4 pt-4 border-t border-zinc-800 space-y-3" data-testid="settlement-preview-step">
              {/* Step Indicator */}
              <div className="flex items-center gap-1 mb-1">
                <div className="flex items-center gap-1 text-[10px]">
                  <span className="px-2 py-0.5 rounded text-zinc-500">Step 1: Upload File</span>
                  <ChevronRight className="w-3 h-3 text-zinc-600" />
                  <span className="px-2 py-0.5 rounded text-zinc-500">Step 2: Confirm Mapping</span>
                  <ChevronRight className="w-3 h-3 text-zinc-600" />
                  <span className="px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-400 font-medium">Step 3: Review & Import</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-300 font-medium">
                  Preview — {uploadState.valid_rows || 0} valid rows, {uploadState.excluded_rows || 0} excluded
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setUploadStep('MAPPING_READY')}
                    className="px-3 py-1.5 text-zinc-400 hover:text-zinc-300 text-xs rounded-lg transition-colors"
                    data-testid="back-to-mapping-btn">
                    Back
                  </button>
                  <button onClick={handleActivate} disabled={uploading || uploadStep === 'ACTIVATING'}
                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs rounded-lg font-medium"
                    data-testid="activate-settlement-btn">
                    {uploadStep === 'ACTIVATING' ? (
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Importing...
                      </span>
                    ) : 'Activate & Import'}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto max-h-60 overflow-y-auto">
                <table className="w-full text-[10px]" data-testid="settlement-preview-table">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500 sticky top-0 bg-zinc-900">
                      <th className="text-left py-1.5 pr-2">#</th>
                      <th className="text-left py-1.5 pr-2">Source</th>
                      <th className="text-left py-1.5 pr-2">Partner</th>
                      <th className="text-left py-1.5 pr-2">Mode</th>
                      <th className="text-left py-1.5 pr-2">Date</th>
                      <th className="text-right py-1.5 pr-2">Net Payout</th>
                      <th className="text-right py-1.5 pr-2">Commission</th>
                      <th className="text-left py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => {
                      const n = row.normalized || {};
                      return (
                        <tr key={row.row_id || row.row_number || `preview-${i}`} className="border-b border-zinc-800/40">
                          <td className="py-1.5 pr-2 text-zinc-500">{row.row_number}</td>
                          <td className="py-1.5 pr-2 text-zinc-300">{row.detected_source}</td>
                          <td className="py-1.5 pr-2 text-zinc-300">{row.detected_partner}</td>
                          <td className="py-1.5 pr-2">
                            {row.detected_mode_of_payment && row.detected_mode_of_payment !== 'UNKNOWN' ? (
                              <Badge variant="outline" className={`text-[8px] ${row.detected_mode_of_payment === 'CARD' ? 'border-blue-500/30 text-blue-400' : row.detected_mode_of_payment === 'UPI' ? 'border-purple-500/30 text-purple-400' : 'border-amber-500/30 text-amber-400'}`}>
                                {row.detected_mode_of_payment}
                              </Badge>
                            ) : <span className="text-zinc-600 text-[9px]">—</span>}
                          </td>
                          <td className="py-1.5 pr-2 text-zinc-400">{n.settlement_date || n.payout_date || n.business_date || '--'}</td>
                          <td className="py-1.5 pr-2 text-right font-mono text-emerald-400">{fmtCur(n.net_payout_amount)}</td>
                          <td className="py-1.5 pr-2 text-right font-mono text-amber-400">{fmtCur(n.commission_amount)}</td>
                          <td className="py-1.5">
                            <Badge variant="outline" className={`text-[8px] ${row.classification === 'VALID' ? 'border-emerald-500/30 text-emerald-400' : 'border-zinc-600 text-zinc-500'}`}>
                              {row.classification}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Error State */}
          {uploadStep === 'ERROR' && uploadError && (
            <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="upload-error-panel">
              <div className="bg-red-950/30 border border-red-500/30 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <p className="text-xs font-medium text-red-400">Failed at: {uploadError.stage}</p>
                </div>
                <p className="text-xs text-red-300">{uploadError.message}</p>
                {uploadError.detail && <p className="text-[10px] text-zinc-500">{uploadError.detail}</p>}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => { setUploadStep('IDLE'); setUploadError(null); setUploadState({}); }}
                    className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded-lg transition-colors"
                    data-testid="retry-upload-btn">
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Complete State */}
          {uploadStep === 'COMPLETE' && (
            <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="upload-complete">
              <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-lg p-4 text-center">
                <Check className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-xs text-emerald-400 font-medium">Import complete. Refreshing data...</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commission Integrity Dashboard */}
      {commIntegrity && commIntegrity.total_comparisons > 0 && (
        <Card className="bg-zinc-900/40 border-amber-800/20" data-testid="commission-integrity">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="w-4 h-4 text-amber-400" />
              <p className="text-sm font-medium text-zinc-200">Commission Integrity</p>
              <Badge variant="outline" className={`text-[9px] ${commIntegrity.net_leakage > 0 ? 'border-red-500/30 text-red-400' : 'border-emerald-500/30 text-emerald-400'}`}>
                {commIntegrity.net_leakage > 0 ? `Leakage: ${fmtCur(commIntegrity.net_leakage)}` : 'No leakage detected'}
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div className="p-2 bg-zinc-800/30 rounded-lg text-center">
                <p className="text-xs font-bold text-zinc-200">{commIntegrity.total_comparisons}</p>
                <p className="text-[9px] text-zinc-500">Total Compared</p>
              </div>
              <div className="p-2 bg-zinc-800/30 rounded-lg text-center">
                <p className="text-xs font-bold font-mono text-zinc-200">{fmtCur(commIntegrity.total_expected_charges)}</p>
                <p className="text-[9px] text-zinc-500">Expected Charges</p>
              </div>
              <div className="p-2 bg-zinc-800/30 rounded-lg text-center">
                <p className="text-xs font-bold font-mono text-amber-400">{fmtCur(commIntegrity.total_actual_charges)}</p>
                <p className="text-[9px] text-zinc-500">Actual Charges</p>
              </div>
              <div className="p-2 bg-zinc-800/30 rounded-lg text-center">
                <p className={`text-xs font-bold font-mono ${commIntegrity.total_overcharge > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {fmtCur(commIntegrity.total_overcharge)}
                </p>
                <p className="text-[9px] text-zinc-500">Overcharged</p>
              </div>
              <div className="p-2 bg-zinc-800/30 rounded-lg text-center">
                <p className={`text-xs font-bold font-mono ${commIntegrity.total_undercharge > 0 ? 'text-emerald-400' : 'text-zinc-400'}`}>
                  {fmtCur(commIntegrity.total_undercharge)}
                </p>
                <p className="text-[9px] text-zinc-500">Undercharged</p>
              </div>
            </div>

            {/* Partner Leakage */}
            {commIntegrity.partner_leakage && Object.keys(commIntegrity.partner_leakage).length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-500 font-medium mb-2">Partner Leakage Breakdown</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {Object.entries(commIntegrity.partner_leakage).map(([p, data]) => (
                    <div key={p} className="p-2.5 bg-zinc-800/30 rounded-lg" data-testid={`partner-leakage-${p}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-semibold ${PARTNER_COLORS[p] || 'text-zinc-400'}`}>{p}</span>
                        <span className={`text-xs font-bold font-mono ${data.net_leakage > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                          {data.net_leakage > 0 ? '+' : ''}{fmtCur(data.net_leakage)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-[9px]">
                        <div><span className="text-zinc-500">Expected</span> <span className="font-mono text-zinc-300">{fmtCur(data.total_expected)}</span></div>
                        <div><span className="text-zinc-500">Actual</span> <span className="font-mono text-amber-400">{fmtCur(data.total_actual)}</span></div>
                        <div><span className="text-zinc-500">Overcharged</span> <span className="font-mono text-red-400">{data.overcharged_count} ({data.overcharge_pct}%)</span></div>
                        <div><span className="text-zinc-500">Orders</span> <span className="text-zinc-300">{data.count}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reconciliation KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Expected Settlement" value={fmtCur(totals.total_expected)} icon={Receipt} color="text-zinc-200" />
        <StatCard label="Actual Received" value={hasData ? fmtCur(totals.total_actual) : '--'} icon={Wallet}
          color={hasData ? 'text-emerald-400' : 'text-zinc-500'} />
        <StatCard label="Variance" value={hasData ? fmtCur(totals.total_variance) : '--'} icon={AlertTriangle}
          color={hasData ? (totals.total_variance >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-zinc-500'}
          sub={hasData ? `${fmtPct(totals.variance_pct)}` : 'No settlement data'} />
        <StatCard label="Buckets" value={recon.length} icon={Building2} sub="partner x model groups" />
      </div>

      {/* Reconciliation Table */}
      <Card className="bg-zinc-900/40 border-zinc-800/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-300">Expected vs Actual — by Commercial Bucket</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="reconciliation-table">
              <thead>
                <tr className="border-b border-zinc-800 text-zinc-500">
                  <th className="text-left py-2.5 pr-3">Bucket</th>
                  <th className="text-right py-2.5 pr-3">Orders</th>
                  <th className="text-right py-2.5 pr-3">Expected</th>
                  <th className="text-right py-2.5 pr-3">Actual</th>
                  <th className="text-right py-2.5 pr-3">Variance</th>
                  <th className="text-right py-2.5 pr-3">Var %</th>
                  <th className="text-right py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleRecon.map(r => {
                  const cfg = getBucket(r.bucket_key);
                  const style = STATUS_STYLES[r.status] || STATUS_STYLES.AWAITING_SETTLEMENT;
                  return (
                    <tr key={r.bucket_key} className="border-b border-zinc-800/40 hover:bg-zinc-800/20" data-testid={`recon-row-${r.bucket_key}`}>
                      <td className={`py-3 pr-3 font-medium ${cfg.color}`}>{r.display_label}</td>
                      <td className="py-3 pr-3 text-right text-zinc-400">{r.expected_orders || '--'}</td>
                      <td className="py-3 pr-3 text-right font-mono text-zinc-200">{fmtCur(r.expected_settlement)}</td>
                      <td className="py-3 pr-3 text-right font-mono text-emerald-400">
                        {r.actual_payout ? fmtCur(r.actual_payout) : '--'}
                      </td>
                      <td className={`py-3 pr-3 text-right font-mono ${varianceColor(r.variance)}`}>
                        {r.actual_payout ? fmtCur(r.variance) : '--'}
                      </td>
                      <td className={`py-3 pr-3 text-right font-mono text-[10px] ${varianceColor(r.variance_pct)}`}>
                        {r.actual_payout ? fmtPct(r.variance_pct) : '--'}
                      </td>
                      <td className="py-3 text-right">
                        <Badge variant="outline" className={`text-[8px] ${style.badge}`}>
                          {r.status?.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
                {/* Totals row */}
                {visibleRecon.length > 0 && (
                  <tr className="border-t-2 border-zinc-700 font-semibold">
                    <td className="py-3 pr-3 text-zinc-300">TOTAL</td>
                    <td className="py-3 pr-3 text-right text-zinc-300">{visibleOrderTotal}</td>
                    <td className="py-3 pr-3 text-right font-mono text-zinc-200">{fmtCur(totals.total_expected)}</td>
                    <td className="py-3 pr-3 text-right font-mono text-emerald-400">{hasData ? fmtCur(totals.total_actual) : '--'}</td>
                    <td className={`py-3 pr-3 text-right font-mono ${totals.total_variance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {hasData ? fmtCur(totals.total_variance) : '--'}
                    </td>
                    <td className={`py-3 pr-3 text-right font-mono text-[10px] ${totals.variance_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {hasData ? fmtPct(totals.variance_pct) : '--'}
                    </td>
                    <td className="py-3"></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {recon.filter(isReconVisible).length === 0 && (
            <p className="text-center text-zinc-600 py-6 text-sm">No transaction data found for this period</p>
          )}
        </CardContent>
      </Card>

      {/* Direct Collections — collected at counter, NOT a platform receivable.
          Surfaced separately so refund / adjustment noise (negative deltas)
          can never pollute the platform reconciliation totals card above. */}
      {(reconData?.direct_collection || []).length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="direct-collections-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
              <Banknote className="w-4 h-4 text-emerald-400" />
              Direct Collections
              <span className="text-[10px] text-zinc-500 font-normal">— collected at counter, not a platform receivable</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {reconData.direct_collection.map(d => {
                const cfg = getBucket(d.bucket_key);
                return (
                  <div key={d.bucket_key} className="p-3 rounded-lg bg-zinc-800/30 border border-zinc-800/50"
                    data-testid={`direct-collection-${d.bucket_key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                      <Badge variant="outline" className="text-[8px] border-zinc-700 text-zinc-500">
                        {d.expected_orders || 0} orders
                      </Badge>
                    </div>
                    <p className="text-base font-mono font-bold text-emerald-400">{fmtCur(d.collection_amount)}</p>
                    {d.refund_adjustment < 0 && (
                      <p className="text-[10px] text-amber-400 mt-1 flex items-center gap-1" data-testid={`refund-adj-${d.bucket_key}`}>
                        <Split className="w-3 h-3" />
                        Refunds / adjustments: {fmtCur(d.refund_adjustment)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── ANOMALY DETECTION SECTION ─── */}
      {anomalies.length > 0 && (
        <>
          {/* Anomaly Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="anomaly-summary-cards">
            <StatCard label="Flagged Anomalies" value={anomalySummary.total_anomalies || 0}
              icon={AlertTriangle} color="text-red-400"
              sub={`${anomalySummary.high_severity || 0} HIGH`} />
            <StatCard label="Total Shortfall" value={fmtCur(anomalySummary.total_shortfall || 0)}
              icon={TrendingUp} color="text-red-400" sub="underpayment" />
            <StatCard label="Excess Deductions" value={fmtCur(anomalySummary.total_excess_deductions || 0)}
              icon={Banknote} color="text-amber-400" sub="over-deducted" />
            <StatCard label="Delayed Settlements" value={anomalySummary.delayed_buckets || 0}
              icon={Clock} color="text-amber-400" sub="past due" />
          </div>

          {/* Anomaly Table */}
          <Card className="bg-zinc-900/40 border-zinc-800/40">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <CardTitle className="text-sm text-zinc-300">Settlement Anomalies</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="anomaly-table">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500">
                      <th className="text-left py-2.5 pr-3">Bucket</th>
                      <th className="text-left py-2.5 pr-3">Type</th>
                      <th className="text-center py-2.5 pr-3">Severity</th>
                      <th className="text-right py-2.5 pr-3">Expected</th>
                      <th className="text-right py-2.5 pr-3">Actual</th>
                      <th className="text-right py-2.5 pr-3">Variance</th>
                      <th className="text-left py-2.5 pr-3">Likely Cause</th>
                      <th className="text-center py-2.5">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anomalies.map((a, i) => {
                      const sevStyle = a.severity === 'HIGH' ? 'bg-red-500/20 text-red-400 border-red-500/30'
                        : a.severity === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                        : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
                      const typeLabel = a.anomaly_type?.replace(/_/g, ' ');
                      const cfg = getBucket(a.bucket_key);
                      const isAmountType = !['UNMATCHED_ROWS_SPIKE'].includes(a.anomaly_type);
                      return (
                        <tr key={`${a.bucket_key}-${a.anomaly_type}-${i}`} className="border-b border-zinc-800/40 hover:bg-zinc-800/20"
                          data-testid={`anomaly-row-${a.bucket_key}-${a.anomaly_type}`}>
                          <td className={`py-3 pr-3 font-medium ${cfg.color}`}>{a.display_label}</td>
                          <td className="py-3 pr-3">
                            <span className="text-[10px] text-zinc-300 font-medium">{typeLabel}</span>
                          </td>
                          <td className="py-3 pr-3 text-center">
                            <Badge variant="outline" className={`text-[8px] ${sevStyle}`}>{a.severity}</Badge>
                          </td>
                          <td className="py-3 pr-3 text-right font-mono text-zinc-300">
                            {isAmountType ? fmtCur(a.expected_amount) : a.expected_amount}
                          </td>
                          <td className="py-3 pr-3 text-right font-mono text-zinc-300">
                            {isAmountType ? fmtCur(a.actual_amount) : a.actual_amount}
                          </td>
                          <td className={`py-3 pr-3 text-right font-mono ${varianceColor(a.variance_amount * -1)}`}>
                            {isAmountType ? fmtCur(a.variance_amount) : `${a.variance_pct}%`}
                          </td>
                          <td className="py-3 pr-3 text-[10px] text-zinc-400 max-w-[250px] truncate" title={a.likely_cause}>
                            {a.likely_cause?.slice(0, 80)}{a.likely_cause?.length > 80 ? '...' : ''}
                          </td>
                          <td className="py-3 text-center">
                            <button onClick={() => handleDrilldown(a.bucket_key)}
                              className="text-[10px] text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
                              data-testid={`drilldown-btn-${a.bucket_key}`}>
                              {drilldown?.bucket_key === a.bucket_key ? 'Close' : 'Drilldown'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Drilldown Panel */}
          {drilldownLoading && (
            <Card className="bg-zinc-900/40 border-zinc-800/40">
              <CardContent className="p-6 text-center text-zinc-500 text-sm">Loading drilldown...</CardContent>
            </Card>
          )}
          {drilldown && !drilldownLoading && (
            <Card className="bg-zinc-900/40 border-emerald-800/30" data-testid="drilldown-panel">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-emerald-400" />
                    <CardTitle className="text-sm text-zinc-300">
                      Drilldown — {drilldown.display_label}
                    </CardTitle>
                  </div>
                  <button onClick={() => setDrilldown(null)} className="text-xs text-zinc-500 hover:text-zinc-300">Close</button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                {/* Drilldown KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                    <p className="text-[10px] text-zinc-500">Total Rows</p>
                    <p className="text-sm font-semibold text-zinc-200">{drilldown.total_rows}</p>
                  </div>
                  <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                    <p className="text-[10px] text-zinc-500">Flagged</p>
                    <p className="text-sm font-semibold text-amber-400">{drilldown.flagged_rows}</p>
                  </div>
                  <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                    <p className="text-[10px] text-zinc-500">Unmatched</p>
                    <p className="text-sm font-semibold text-red-400">{drilldown.unmatched_rows}</p>
                  </div>
                  <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                    <p className="text-[10px] text-zinc-500">High Deductions</p>
                    <p className="text-sm font-semibold text-amber-400">{drilldown.high_deduction_rows}</p>
                  </div>
                  <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                    <p className="text-[10px] text-zinc-500">Refund Rows</p>
                    <p className="text-sm font-semibold text-red-400">{drilldown.refund_rows}</p>
                  </div>
                </div>

                {/* Top deduction rows */}
                {drilldown.top_deduction_rows?.length > 0 && (
                  <div>
                    <p className="text-[11px] text-zinc-400 font-medium mb-2">Top Deduction Rows</p>
                    <div className="overflow-x-auto max-h-48 overflow-y-auto">
                      <table className="w-full text-[10px]" data-testid="drilldown-deduction-table">
                        <thead>
                          <tr className="border-b border-zinc-800 text-zinc-500 sticky top-0 bg-zinc-900">
                            <th className="text-left py-1.5 pr-2">Date</th>
                            <th className="text-left py-1.5 pr-2">Reference</th>
                            <th className="text-right py-1.5 pr-2">Gross</th>
                            <th className="text-right py-1.5 pr-2">Commission</th>
                            <th className="text-right py-1.5 pr-2">Deductions</th>
                            <th className="text-right py-1.5 pr-2">Net Payout</th>
                            <th className="text-left py-1.5">Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drilldown.top_deduction_rows.map((r, i) => (
                            <tr key={r.settlement_row_id || r.transaction_reference || `ded-${i}`} className="border-b border-zinc-800/40">
                              <td className="py-1.5 pr-2 text-zinc-400">{r.settlement_date || r.business_date || '--'}</td>
                              <td className="py-1.5 pr-2 text-zinc-300">{r.transaction_reference || r.invoice_number || '--'}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-zinc-300">{fmtCur(r.gross_settlement_amount)}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-amber-400">{fmtCur(r.commission_amount)}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-red-400">{fmtCur(r.other_deductions)}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-emerald-400">{fmtCur(r.net_payout_amount)}</td>
                              <td className="py-1.5 text-zinc-500 max-w-[150px] truncate" title={r.remarks}>{r.remarks || '--'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* All settlement rows */}
                {drilldown.all_rows?.length > 0 && (
                  <div>
                    <p className="text-[11px] text-zinc-400 font-medium mb-2">
                      All Settlement Rows ({drilldown.total_rows})
                      {drilldown.suspicious_rows > 0 && <span className="text-red-400 ml-2">{drilldown.suspicious_rows} suspicious</span>}
                    </p>
                    <div className="overflow-x-auto max-h-60 overflow-y-auto">
                      <table className="w-full text-[10px]" data-testid="drilldown-all-rows-table">
                        <thead>
                          <tr className="border-b border-zinc-800 text-zinc-500 sticky top-0 bg-zinc-900">
                            <th className="text-left py-1.5 pr-2">Date</th>
                            <th className="text-left py-1.5 pr-2">Reference</th>
                            <th className="text-right py-1.5 pr-2">Gross</th>
                            <th className="text-right py-1.5 pr-2">Commission</th>
                            <th className="text-right py-1.5 pr-2">TDS</th>
                            <th className="text-right py-1.5 pr-2">Deductions</th>
                            <th className="text-right py-1.5 pr-2">Refunds</th>
                            <th className="text-right py-1.5 pr-2">Net Payout</th>
                            <th className="text-left py-1.5">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drilldown.all_rows.map((r, i) => (
                            <tr key={r.settlement_row_id || r.transaction_reference || `row-${i}`} className="border-b border-zinc-800/40">
                              <td className="py-1.5 pr-2 text-zinc-400">{r.settlement_date || r.business_date || '--'}</td>
                              <td className="py-1.5 pr-2 text-zinc-300 max-w-[100px] truncate">{r.transaction_reference || r.invoice_number || '--'}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-zinc-300">{fmtCur(r.gross_settlement_amount)}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-amber-400">{fmtCur(r.commission_amount)}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-zinc-500">{fmtCur(r.tds_amount)}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-red-400">{fmtCur(r.other_deductions)}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-red-400">{fmtCur(r.refunds)}</td>
                              <td className="py-1.5 pr-2 text-right font-mono text-emerald-400">{fmtCur(r.net_payout_amount)}</td>
                              <td className="py-1.5">
                                <Badge variant="outline" className={`text-[8px] ${r.match_status === 'MATCHED' ? 'border-emerald-500/30 text-emerald-400' : 'border-amber-500/30 text-amber-400'}`}>
                                  {r.match_status || 'UNMATCHED'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
      {uploads.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300">Settlement Upload History</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {uploads.slice(0, 5).map(u => (
                <div key={u.upload_id} className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg" data-testid={`upload-${u.upload_id?.slice(0, 8)}`}>
                  <div className="flex items-center gap-3">
                    <Landmark className="w-4 h-4 text-zinc-500" />
                    <div>
                      <p className="text-xs text-zinc-300">{u.file_name}</p>
                      <p className="text-[10px] text-zinc-500">{u.total_rows} rows &middot; {u.created_at?.slice(0, 10)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.total_payout && <span className="text-xs font-mono text-emerald-400">{fmtCur(u.total_payout)}</span>}
                    <Badge variant="outline" className={`text-[8px] ${u.status === 'ACTIVATED' ? 'border-emerald-500/30 text-emerald-400' : 'border-zinc-600 text-zinc-500'}`}>
                      {u.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SETTLEMENT MATCHING TAB — Exception-First Dashboard
// ═══════════════════════════════════════════════════════════════

const EXCEPTION_TABS = [
  { id: 'ALL', label: 'All Exceptions' },
  { id: 'OVERDUE', label: 'Overdue' },
  { id: 'SHORTFALL', label: 'Shortfall' },
  { id: 'EXCESS', label: 'Excess' },
  { id: 'UNMATCHED_ROW', label: 'Unmatched Rows' },
  { id: 'MANY_TO_ONE_REVIEW', label: 'Many-to-One' },
  { id: 'ONE_TO_MANY_REVIEW', label: 'One-to-Many' },
];

const MATCH_STATUS_ICON = {
  MATCHED: { icon: CheckCircle2, color: 'text-emerald-400' },
  SHORTFALL: { icon: TrendingUp, color: 'text-red-400' },
  EXCESS: { icon: TrendingUp, color: 'text-blue-400' },
  DELAYED: { icon: Clock, color: 'text-yellow-400' },
  OVERDUE: { icon: ShieldAlert, color: 'text-red-400' },
  UNMATCHED_ROW: { icon: Search, color: 'text-purple-400' },
  MANY_TO_ONE_REVIEW: { icon: Layers, color: 'text-orange-400' },
  ONE_TO_MANY_REVIEW: { icon: Split, color: 'text-cyan-400' },
};

const MatchingTab = ({ dateFrom, dateTo }) => {
  const [matchSummary, setMatchSummary] = useState(null);
  const [healthScore, setHealthScore] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [tabCounts, setTabCounts] = useState({});
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [overrideMatch, setOverrideMatch] = useState(null);
  const [overrideAction, setOverrideAction] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const exParams = { ...params, limit: 100 };
      if (activeFilter !== 'ALL') exParams.exception_type = activeFilter;

      const [summaryRes, exceptionsRes, healthRes] = await Promise.all([
        accountsAPI.getMatchingSummary(params),
        accountsAPI.getMatchingExceptions(exParams),
        accountsAPI.getHealthScore(params),
      ]);

      setMatchSummary(summaryRes.data);
      setExceptions(exceptionsRes.data?.exceptions || []);
      setTabCounts(exceptionsRes.data?.tab_counts || {});
      setHealthScore(healthRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, activeFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRunMatching = async () => {
    setRunning(true);
    try {
      const body = {};
      if (dateFrom) body.date_from = dateFrom;
      if (dateTo) body.date_to = dateTo;
      const res = await accountsAPI.runSettlementMatching(body);
      toast.success(`Matching complete: ${res.data?.summary?.total_matches || 0} results`);
      await loadData();
    } catch (e) {
      toast.error('Matching failed');
    } finally {
      setRunning(false);
    }
  };

  const handleViewDetail = async (matchId) => {
    if (selectedMatch?.match_id === matchId) { setSelectedMatch(null); return; }
    setDetailLoading(true);
    try {
      const res = await accountsAPI.getExceptionDetail(matchId);
      setSelectedMatch(res.data);
    } catch (e) {
      toast.error('Failed to load detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOverride = async () => {
    if (!overrideAction || !overrideReason.trim()) {
      toast.error('Action and reason are required');
      return;
    }
    setOverrideSubmitting(true);
    try {
      await accountsAPI.applyMatchOverride({
        match_id: overrideMatch.match_id,
        action: overrideAction,
        reason: overrideReason,
      });
      toast.success('Override applied');
      setOverrideMatch(null);
      setOverrideAction('');
      setOverrideReason('');
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Override failed');
    } finally {
      setOverrideSubmitting(false);
    }
  };

  if (loading && !matchSummary) return <LoadingSkeleton />;

  const sb = matchSummary?.status_breakdown || {};
  const totalExceptions = matchSummary?.exception_count || 0;
  const totalOverdue = sb.OVERDUE?.count || 0;
  const totalShortfall = sb.SHORTFALL?.count || 0;
  const totalMatched = sb.MATCHED?.count || 0;

  return (
    <div className="space-y-5" data-testid="matching-tab">
      {/* Stale-results banner — fires when settlements were activated
          or quarantined after the last matching run. Without this,
          operators can mistake stale OVERDUE counts for reality. */}
      <StaleMatchingBanner
        needsRerun={matchSummary?.needs_rerun}
        reason={matchSummary?.stale_reason}
        onRunMatching={handleRunMatching}
        busy={running}
      />

      {/* Run Matching + Summary Strip */}
      <Card className="bg-zinc-900/40 border-zinc-800/40">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-sm font-medium text-zinc-200">Row-Level Settlement Matching</p>
                <p className="text-[11px] text-zinc-500">
                  {matchSummary?.last_run?.run_id
                    ? `Last run: ${matchSummary.last_run.computed_at?.slice(0, 16)?.replace('T', ' ')} (v${matchSummary.last_run.matching_version})`
                    : 'No matching runs yet'
                  }
                </p>
              </div>
            </div>
            <button onClick={handleRunMatching} disabled={running}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs rounded-lg transition-colors"
              data-testid="run-matching-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Running...' : 'Run Matching'}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="matching-kpis">
        <StatCard label="Total Matches" value={matchSummary?.total_matches || 0} icon={Link2} color="text-zinc-200"
          sub={`v${matchSummary?.last_run?.matching_version || '?'}`} />
        <StatCard label="Matched" value={totalMatched} icon={CheckCircle2} color="text-emerald-400"
          sub={`${matchSummary?.total_matches ? Math.round(totalMatched / matchSummary.total_matches * 100) : 0}%`} />
        <StatCard label="Exceptions" value={totalExceptions} icon={AlertTriangle} color="text-red-400"
          sub="needs attention" />
        <StatCard label="Overdue" value={totalOverdue} icon={ShieldAlert} color="text-red-400"
          sub={`₹${fmt(sb.OVERDUE?.total_expected || 0, 0)}`} />
        <StatCard label="Shortfall" value={totalShortfall} icon={TrendingUp} color="text-amber-400"
          sub={fmtCur(Math.abs(sb.SHORTFALL?.total_variance || 0))} />
      </div>

      {/* Settlement Health Score */}
      {healthScore && (
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="health-score-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-16 h-16">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" stroke="currentColor" strokeWidth="8" fill="none" className="text-zinc-800" />
                  <circle cx="50" cy="50" r="42" stroke="currentColor" strokeWidth="8" fill="none"
                    strokeDasharray={`${(healthScore.health_score / 100) * 264} 264`}
                    strokeLinecap="round"
                    className={scoreColor(healthScore.health_score)} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-base font-bold ${scoreColor(healthScore.health_score)}`}>
                    {healthScore.health_score}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-200">Settlement Health Score</p>
                <Badge variant="outline" className={`text-[9px] mt-0.5 ${
                  healthScore.grade === 'EXCELLENT' ? 'border-emerald-500/30 text-emerald-400' :
                  healthScore.grade === 'GOOD' ? 'border-green-500/30 text-green-400' :
                  healthScore.grade === 'NEEDS_ATTENTION' ? 'border-amber-500/30 text-amber-400' :
                  'border-red-500/30 text-red-400'
                }`}>{healthScore.grade?.replace(/_/g, ' ')}</Badge>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  Platform expected: {fmtCur(healthScore.totals?.platform_expected || 0)} | Matched: {fmtCur(healthScore.totals?.platform_matched || 0)}
                </p>
              </div>
            </div>

            {/* Score Components */}
            <div className="grid grid-cols-5 gap-2 mb-4">
              {healthScore.components && Object.entries(healthScore.components).map(([key, comp]) => (
                <div key={key} className="p-2 bg-zinc-800/30 rounded-lg text-center">
                  <p className={`text-sm font-bold ${scoreColor(comp.score)}`}>
                    {comp.score}
                  </p>
                  <p className="text-[8px] text-zinc-500 mt-0.5 leading-tight">{key.replace(/_/g, ' ')}</p>
                  <p className="text-[8px] text-zinc-600">w: {comp.weight}%</p>
                </div>
              ))}
            </div>

            {/* Partner Health Drilldown */}
            {healthScore.partner_drilldown && Object.keys(healthScore.partner_drilldown).length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-500 font-medium mb-2">Partner Health</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {Object.entries(healthScore.partner_drilldown).map(([partner, data]) => {
                    const pColor = PARTNER_COLORS[partner] || 'text-zinc-400';
                    const scoreClr = scoreColor(data.health_score);
                    return (
                      <div key={partner} className="p-2.5 bg-zinc-800/30 rounded-lg" data-testid={`partner-health-${partner}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={`text-xs font-semibold ${pColor}`}>{partner}</span>
                          <span className={`text-sm font-bold ${scoreClr}`}>{data.health_score}</span>
                        </div>
                        <div className="w-full bg-zinc-700 rounded-full h-1 mb-1.5">
                          <div className={`h-1 rounded-full ${scoreColor(data.health_score, 'bg')}`}
                            style={{ width: `${data.health_score}%` }} />
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-[9px]">
                          <div><span className="text-zinc-500">Expected</span> <span className="text-zinc-300 font-mono">{fmtCur(data.total_expected)}</span></div>
                          <div><span className="text-zinc-500">Matched</span> <span className="text-emerald-400 font-mono">{fmtCur(data.total_matched)}</span></div>
                          <div><span className="text-zinc-500">Overdue</span> <span className="text-red-400 font-mono">{fmtCur(data.total_overdue)}</span></div>
                          <div><span className="text-zinc-500">Shortfall</span> <span className="text-amber-400 font-mono">{fmtCur(data.total_shortfall)}</span></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Partner Status Breakdown */}
      {matchSummary?.partner_breakdown && Object.keys(matchSummary.partner_breakdown).filter(p => p !== 'NONE').length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-zinc-400 mb-3">Partner Status Breakdown</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(matchSummary.partner_breakdown)
                .filter(([p]) => p !== 'NONE')
                .map(([partner, statuses]) => {
                  const pColor = PARTNER_COLORS[partner] || 'text-zinc-400';
                  return (
                    <div key={partner} className="p-3 bg-zinc-800/30 rounded-lg" data-testid={`partner-breakdown-${partner}`}>
                      <p className={`text-xs font-semibold ${pColor} mb-2`}>{partner}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(statuses).map(([status, data]) => {
                          const style = STATUS_STYLES[status] || STATUS_STYLES.AWAITING_SETTLEMENT;
                          return (
                            <Badge key={status} variant="outline" className={`text-[9px] ${style.badge}`}>
                              {status.replace(/_/g, ' ')} ({data.count})
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exception Queue Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-0 overflow-x-auto" data-testid="exception-tabs">
        {EXCEPTION_TABS.map(tab => {
          const count = tab.id === 'ALL' ? totalExceptions : (tabCounts[tab.id] || 0);
          const isActive = activeFilter === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveFilter(tab.id)}
              className={`flex items-center gap-1 px-3 py-2 text-[11px] font-medium transition-colors border-b-2 -mb-[1px] whitespace-nowrap ${
                isActive
                  ? 'text-emerald-400 border-emerald-400'
                  : 'text-zinc-500 border-transparent hover:text-zinc-300'
              }`}
              data-testid={`exception-tab-${tab.id}`}>
              {tab.label}
              {count > 0 && (
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Exception Table */}
      <Card className="bg-zinc-900/40 border-zinc-800/40">
        <CardContent className="p-0">
          {exceptions.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">
                {matchSummary?.total_matches ? 'No exceptions in this category' : 'Run matching to see results'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="exception-table">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500">
                    <th className="text-left py-3 px-3">Status</th>
                    <th className="text-left py-3 px-2">Txn / Ref</th>
                    <th className="text-left py-3 px-2">Partner</th>
                    <th className="text-left py-3 px-2">Date</th>
                    <th className="text-right py-3 px-2">Expected</th>
                    <th className="text-right py-3 px-2">Actual</th>
                    <th className="text-right py-3 px-2">Variance</th>
                    <th className="text-center py-3 px-2">Delay</th>
                    <th className="text-center py-3 px-2">Conf.</th>
                    <th className="text-left py-3 px-2">Reason</th>
                    <th className="text-center py-3 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exceptions.map((ex) => {
                    const si = MATCH_STATUS_ICON[ex.match_status] || MATCH_STATUS_ICON.OVERDUE;
                    const StatusIcon = si.icon;
                    const style = STATUS_STYLES[ex.match_status] || STATUS_STYLES.AWAITING_SETTLEMENT;
                    return (
                      <tr key={ex.match_id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20"
                        data-testid={`exception-row-${ex.match_id?.slice(0, 8)}`}>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1.5">
                            <StatusIcon className={`w-3.5 h-3.5 ${si.color}`} />
                            <Badge variant="outline" className={`text-[8px] ${style.badge}`}>
                              {ex.match_status?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-zinc-300 font-mono text-[10px] max-w-[120px] truncate" title={ex.transaction_id || ex.settlement_row_id}>
                          {ex.transaction_id?.replace('TXN-', '') || ex.settlement_row_id?.slice(0, 12) || '--'}
                        </td>
                        <td className={`py-3 px-2 ${PARTNER_COLORS[ex.partner] || 'text-zinc-400'}`}>
                          {ex.partner}
                          <span className="text-[9px] text-zinc-600 block">{ex.commercial_model?.replace(/_/g, ' ')}</span>
                        </td>
                        <td className="py-3 px-2 text-zinc-400 text-[10px]">{ex.business_date || '--'}</td>
                        <td className="py-3 px-2 text-right font-mono text-zinc-200">{fmtCur(ex.expected_settlement_amount)}</td>
                        <td className="py-3 px-2 text-right font-mono text-emerald-400">
                          {ex.actual_settlement_amount ? fmtCur(ex.actual_settlement_amount) : '--'}
                        </td>
                        <td className={`py-3 px-2 text-right font-mono ${ex.variance_amount < 0 ? 'text-red-400' : ex.variance_amount > 0 ? 'text-blue-400' : 'text-zinc-500'}`}>
                          {ex.variance_amount ? fmtCur(ex.variance_amount) : '--'}
                        </td>
                        <td className="py-3 px-2 text-center">
                          {ex.delay_days > 0 ? (
                            <span className={`text-[10px] font-semibold ${ex.delay_days > 7 ? 'text-red-400' : 'text-yellow-400'}`}>
                              {ex.delay_days}d
                            </span>
                          ) : '--'}
                        </td>
                        <td className="py-3 px-2 text-center">
                          {ex.confidence_score > 0 ? (
                            <span className={`text-[10px] font-mono ${scoreColor(ex.confidence_score)}`}>
                              {ex.confidence_score}%
                            </span>
                          ) : '--'}
                        </td>
                        <td className="py-3 px-2 text-[10px] text-zinc-500 max-w-[180px] truncate" title={ex.reason_detail}>
                          {ex.reason_detail?.slice(0, 60)}{ex.reason_detail?.length > 60 ? '...' : ''}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <button onClick={() => handleViewDetail(ex.match_id)}
                              className="p-1 hover:bg-zinc-700 rounded text-zinc-400 hover:text-zinc-200 transition-colors"
                              title="View details" data-testid={`view-detail-${ex.match_id?.slice(0, 8)}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => { setOverrideMatch(ex); setOverrideAction(''); setOverrideReason(''); }}
                              className="p-1 hover:bg-zinc-700 rounded text-zinc-400 hover:text-zinc-200 transition-colors"
                              title="Override" data-testid={`override-btn-${ex.match_id?.slice(0, 8)}`}>
                              <Check className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Panel */}
      {detailLoading && (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardContent className="p-6 text-center text-zinc-500 text-sm">Loading detail...</CardContent>
        </Card>
      )}
      {selectedMatch && !detailLoading && (
        <Card className="bg-zinc-900/40 border-emerald-800/30" data-testid="match-detail-panel">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-emerald-400" />
                <p className="text-sm font-medium text-zinc-200">Match Detail</p>
                <Badge variant="outline" className={`text-[8px] ${(STATUS_STYLES[selectedMatch.match_status] || STATUS_STYLES.AWAITING_SETTLEMENT).badge}`}>
                  {selectedMatch.match_status?.replace(/_/g, ' ')}
                </Badge>
              </div>
              <button onClick={() => setSelectedMatch(null)} className="text-xs text-zinc-500 hover:text-zinc-300">Close</button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                <p className="text-[10px] text-zinc-500">Transaction ID</p>
                <p className="text-xs font-mono text-zinc-200">{selectedMatch.transaction_id || '--'}</p>
              </div>
              <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                <p className="text-[10px] text-zinc-500">Settlement Row</p>
                <p className="text-xs font-mono text-zinc-200">{selectedMatch.settlement_row_id?.slice(0, 16) || 'Not matched'}</p>
              </div>
              <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                <p className="text-[10px] text-zinc-500">Confidence</p>
                <p className={`text-sm font-semibold ${scoreColor(selectedMatch.confidence_score)}`}>
                  {selectedMatch.confidence_score}%
                </p>
              </div>
              <div className="p-2.5 bg-zinc-800/40 rounded-lg">
                <p className="text-[10px] text-zinc-500">Delay Days</p>
                <p className={`text-sm font-semibold ${selectedMatch.delay_days > 7 ? 'text-red-400' : 'text-zinc-200'}`}>
                  {selectedMatch.delay_days || 0}
                </p>
              </div>
            </div>

            {/* Expected vs Actual Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                <p className="text-[10px] text-zinc-500 font-medium mb-2">Expected Settlement</p>
                {selectedMatch.expected_breakdown ? (
                  <div className="space-y-1 text-[10px]">
                    <div className="flex justify-between"><span className="text-zinc-400">Collectible</span><span className="font-mono text-zinc-200">{fmtCur(selectedMatch.expected_breakdown.collectible_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-400">Commission</span><span className="font-mono text-amber-400">-{fmtCur(selectedMatch.expected_breakdown.expected_commission)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-400">GST on Comm.</span><span className="font-mono text-amber-400">-{fmtCur(selectedMatch.expected_breakdown.expected_tax_on_commission)}</span></div>
                    <div className="flex justify-between border-t border-zinc-700 pt-1 mt-1"><span className="text-zinc-300 font-medium">Net Expected</span><span className="font-mono text-emerald-400 font-semibold">{fmtCur(selectedMatch.expected_settlement_amount)}</span></div>
                  </div>
                ) : <p className="text-xs font-mono text-emerald-400">{fmtCur(selectedMatch.expected_settlement_amount)}</p>}
              </div>

              <div className="p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                <p className="text-[10px] text-zinc-500 font-medium mb-2">Actual Settlement</p>
                {selectedMatch.settlement_details ? (
                  <div className="space-y-1 text-[10px]">
                    <div className="flex justify-between"><span className="text-zinc-400">Gross</span><span className="font-mono text-zinc-200">{fmtCur(selectedMatch.settlement_details.gross_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-400">Commission</span><span className="font-mono text-amber-400">-{fmtCur(selectedMatch.settlement_details.commission_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-400">Other Ded.</span><span className="font-mono text-red-400">-{fmtCur(selectedMatch.settlement_details.other_deductions)}</span></div>
                    <div className="flex justify-between border-t border-zinc-700 pt-1 mt-1"><span className="text-zinc-300 font-medium">Net Actual</span><span className="font-mono text-emerald-400 font-semibold">{fmtCur(selectedMatch.actual_settlement_amount)}</span></div>
                  </div>
                ) : <p className="text-xs text-zinc-500">No settlement data</p>}
              </div>
            </div>

            {/* Commission Comparison */}
            {selectedMatch.commission_comparison && (
              <div className="p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                <p className="text-[10px] text-zinc-500 font-medium mb-2">Commission Comparison</p>
                <div className="grid grid-cols-3 gap-3 text-[10px]">
                  <div>
                    <p className="text-zinc-500">Expected</p>
                    <p className="font-mono text-zinc-200">{fmtCur(selectedMatch.commission_comparison.expected_commission)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Actual</p>
                    <p className="font-mono text-zinc-200">{fmtCur(selectedMatch.commission_comparison.actual_commission)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Variance</p>
                    <p className={`font-mono font-semibold ${varianceColorAlt(selectedMatch.commission_comparison.commission_variance)}`}>
                      {fmtCur(selectedMatch.commission_comparison.commission_variance)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="p-3 bg-zinc-800/30 rounded-lg">
              <p className="text-[10px] text-zinc-500 mb-1">Reason</p>
              <p className="text-xs text-zinc-300">{selectedMatch.reason_detail}</p>
              <Badge variant="outline" className="text-[8px] border-zinc-700 text-zinc-500 mt-1">{selectedMatch.reason_code}</Badge>
            </div>

            {/* Suggested Matches (for UNMATCHED_ROW) */}
            {selectedMatch.suggested_matches?.length > 0 && (
              <div className="p-3 bg-zinc-800/30 rounded-lg">
                <p className="text-[10px] text-zinc-500 font-medium mb-2">Suggested Transaction Matches</p>
                <div className="space-y-1">
                  {selectedMatch.suggested_matches.map((s, i) => (
                    <div key={s.transaction_id || `sugg-${i}`} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded text-[10px]">
                      <span className="font-mono text-zinc-300">{s.transaction_id}</span>
                      <span className="text-zinc-400">{s.date}</span>
                      <span className="font-mono text-emerald-400">{fmtCur(s.expected_settlement)}</span>
                      <span className="text-zinc-500">diff: {fmtCur(s.amount_diff)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manual Override */}
            {selectedMatch.manual_override && (
              <div className="p-3 bg-amber-950/20 border border-amber-500/20 rounded-lg">
                <p className="text-[10px] text-amber-400 font-medium">Manual Override Applied</p>
                <p className="text-[10px] text-zinc-400 mt-1">
                  {selectedMatch.manual_override.action} by {selectedMatch.manual_override.performed_by} at {selectedMatch.manual_override.performed_at?.slice(0, 16)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Override Modal */}
      {overrideMatch && (
        <Card className="bg-zinc-900/40 border-amber-800/30" data-testid="override-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-zinc-200">Apply Override — {overrideMatch.transaction_id || overrideMatch.settlement_row_id?.slice(0, 16)}</p>
              <button onClick={() => setOverrideMatch(null)} className="text-xs text-zinc-500 hover:text-zinc-300">Cancel</button>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Action</p>
              <select value={overrideAction} onChange={e => setOverrideAction(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-lg px-3 py-2"
                data-testid="override-action-select">
                <option value="">Select action...</option>
                <option value="confirm_match">Confirm Match</option>
                <option value="reject_suggestion">Reject Suggestion</option>
                <option value="mark_aggregation">Mark as Aggregation (Many-to-One)</option>
                <option value="mark_split">Mark as Split (One-to-Many)</option>
              </select>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Reason (required)</p>
              <Input value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                placeholder="Explain why this override is being applied..."
                className="bg-zinc-800 border-zinc-700 text-xs"
                data-testid="override-reason-input" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setOverrideMatch(null)} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200">Cancel</button>
              <button onClick={handleOverride} disabled={overrideSubmitting || !overrideAction || !overrideReason.trim()}
                className="px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs rounded-lg transition-colors"
                data-testid="apply-override-btn">
                {overrideSubmitting ? 'Applying...' : 'Apply Override'}
              </button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── Loading Skeleton ───
const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
    <div className="grid grid-cols-2 gap-3">{[1,2].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
    <Skeleton className="h-64 rounded-xl" />
  </div>
);

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function TransactionDashboardPage() {
  const [activeTab, setActiveTab] = useState('transactions');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const periodState = usePeriod(PERIOD_MODES.RANGE);
  const dateFrom = periodState.period.from;
  const dateTo = periodState.period.to;

  const begin = useLatestRequest();
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    const isLatest = begin();
    setLoading(true);
    setSummary(null);
    setTransactions([]);
    try {
      const params = periodState.apiParams;

      const [sumRes, txnRes] = await Promise.all([
        accountsAPI.getTransactionSummary(params),
        accountsAPI.getTransactions({ ...params, limit: 50 }),
      ]);
      if (!isLatest()) return;
      setSummary(sumRes.data);
      setTransactions(txnRes.data?.transactions || []);
    } catch (e) {
      if (isLatest()) toast.error('Failed to load transaction data');
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [periodState.apiParams, begin]);

  const exportAll = async () => {
    setExporting(true);
    try {
      const rows = [];
      for (let skip = 0; ; skip += 200) {
        const r = await accountsAPI.getTransactions({ ...periodState.apiParams, limit: 200, skip });
        rows.push(...(r.data?.transactions || []));
        if (rows.length >= (r.data?.total || 0) || !(r.data?.transactions || []).length) break;
      }
      downloadCsv(`transactions-${dateFrom}-to-${dateTo}.csv`, [
        { label: 'Transaction ID', value: 'sc_transaction_id' }, { label: 'Business date', value: 'business_date' },
        { label: 'Channel', value: 'channel' }, { label: 'Partner', value: 'partner' },
        { label: 'Commercial model', value: 'commercial_model' }, { label: 'Gross sales', value: 'gross_sales' },
        { label: 'Net sales', value: 'net_sales' }, { label: 'Taxes', value: 'taxes_total' },
        { label: 'Final total', value: 'final_total' }, { label: 'Paid', value: 'paid_amount' },
      ], rows);
      toast.success(`Exported ${rows.length} transactions`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="space-y-5" data-testid="transaction-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2.5">
            <BarChart3 className="w-6 h-6 text-emerald-400" />
            Transaction Intelligence
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Orders, settlements, partner analytics, and reconciliation
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="date-filters">
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE} resolved={summary} />
          <button onClick={exportAll} disabled={exporting || loading || !summary}
            className="h-8 px-3 rounded-md border border-zinc-700 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
            data-testid="transactions-export-btn">
            {exporting ? 'Exporting…' : `Export ${summary?.totals?.total_orders ?? ''} transactions`}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-0" data-testid="dashboard-tabs">
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
                isActive
                  ? 'text-emerald-400 border-emerald-400'
                  : 'text-zinc-500 border-transparent hover:text-zinc-300 hover:border-zinc-600'
              }`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'transactions' && <TransactionsTab summary={summary} transactions={transactions} loading={loading} />}
      {activeTab === 'settlements' && <SettlementsTab summary={summary} loading={loading} />}
      {activeTab === 'partners' && <PartnersTab summary={summary} loading={loading} />}
      {activeTab === 'reconciliation' && <ReconciliationTab summary={summary} loading={loading} dateFrom={dateFrom} dateTo={dateTo} />}
      {activeTab === 'matching' && <MatchingTab dateFrom={dateFrom} dateTo={dateTo} />}
    </div>
  );
}
